/**
 * Whether a return stands on an acknowledgment number alone (FR-9).
 *
 * Marked filed — `record_filing` took somebody's ARN — and never confirmed by
 * the portal's own filed return. A snapshot is never unconfirmed: it was
 * imported FROM the filed return, so it is its own receipt.
 *
 * One rule for the two places the Returns screen asks it — the `· unconfirmed`
 * badge in the rail and the list above it — so the badge and the list cannot
 * disagree, and so a test can hold it (`tests/filing.test.ts`). The service's
 * `filed_unconfirmed` filter is the same rule on the other side.
 */

import type { ErrorReportUpload, PeriodRecord, PortalRejection } from './api'

export function isUnconfirmed(
  row: Pick<PeriodRecord, 'state' | 'origin' | 'filed_confirmed_at'>,
): boolean {
  return (
    (row.state === 'filed' || row.state === 'locked') &&
    row.origin !== 'snapshot' &&
    !row.filed_confirmed_at
  )
}

/**
 * Which return can take the portal's error report (FR-E6).
 *
 * The GSTN JSON is written from client-approved data only (FR-44), so the portal can reject
 * only what a `client_approved` GSTR-1 uploaded — and a rejection moves nothing on the portal,
 * so the return is still `client_approved` when the report comes back. A return marked filed
 * stands on an acknowledgment number the portal issued, which it does not issue for a
 * rejected upload: that return gets *Confirm with the filed return*, and the service refuses
 * an error report there (FR-9). A 3B and a snapshot never uploaded a GSTR-1.
 */
export function canReceiveErrorReport(
  row: Pick<PeriodRecord, 'state' | 'origin' | 'return_type'>,
): boolean {
  return row.return_type === 'GSTR1' && row.state === 'client_approved' && row.origin !== 'snapshot'
}

const SECTION_WORDS: Record<string, string> = {
  b2b: 'B2B',
  b2cs: 'B2CS',
  cdnr: 'Credit/debit notes',
}

/** One of the portal's errors, as the service's Activity says it. */
export function rejectionWords(r: PortalRejection): string {
  const section = SECTION_WORDS[r.section] ?? r.section
  const where =
    r.section === 'b2cs'
      ? `state ${r.pos ?? '?'} at ${r.rate ?? '?'}% (${section})`
      : `${[r.ctin, r.doc_no].filter(Boolean).join(' · ')} (${section})`
  const message = (r.error_msg ?? '').trim()
  const code = (r.error_cd ?? '').trim()
  const said = message && code ? `${message} (${code})` : message || code || 'no message'
  return `${where}: ${said}`
}

/**
 * The result sentence: "Portal error report uploaded: 3 rows held, 1 error could not be
 * matched to a row. The return is back at Prepared." — the same first clause the service
 * writes on Activity and on the demotion.
 */
export function errorReportWords(
  r: Pick<ErrorReportUpload, 'rows_held' | 'errors_unmatched' | 'demoted' | 'state'>,
): string {
  const rows = `${r.rows_held} row${r.rows_held === 1 ? '' : 's'} held`
  const errors = r.errors_unmatched
    ? `${r.errors_unmatched} error${r.errors_unmatched === 1 ? '' : 's'} could not be matched to a row`
    : 'every error matched a row'
  const back = r.demoted
    ? 'The return is back at Prepared — correct the held rows, then send it for approval again.'
    : r.state === 'prepared'
      ? 'The return was already at Prepared.'
      : 'The return did not move.'
  return `Portal error report uploaded: ${rows}, ${errors}. ${back}`
}
